import { useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H5Regular,
    IconButton,
    LabelRegular,
    PercentInput,
    Trash2,
    XStack,
    YStack,
} from 'smoothie';

import { type User } from '../../../user/types/User';
import {
    type AccountDetailWithBeneficiaries,
    type BeneficiaryAccountDto,
} from '../../types/Beneficiaries';
import { RemoveBeneficiaryConfirmation } from './RemoveBeneficiaryConfirmation';

interface CoreBeneficiariesProps {
    user: User;
    detail: AccountDetailWithBeneficiaries;
    onSave: (
        updates: Array<{
            id: string;
            bankAccountId: string;
            percentage: number;
        }>,
    ) => void;
    onDelete: (id: string) => Promise<void>;
    onDeleteFromAllAccounts: (id: string) => Promise<void>;
    isSaving?: boolean;
}

const toDisplayValue = (percentage: number): string =>
    percentage > 0 ? `${percentage} %` : '';

const fromDisplayValue = (value: string): number =>
    parseInt(value.replace(/\s*%\s*$/, ''), 10) || 0;

export const ManageAccountBeneficiaries = ({
    user,
    detail,
    onSave,
    onDelete,
    onDeleteFromAllAccounts,
    isSaving = false,
}: CoreBeneficiariesProps) => {
    const partner = user.partnerMember;
    const names = new Map<string, string>();
    names.set(user.id, 'You');
    if (partner?.id != null) {
        names.set(partner.id, partner.firstName ?? '');
    }

    const [rows, setRows] = useState<
        Array<BeneficiaryAccountDto & { displayValue: string }>
    >(() =>
        detail.beneficiaries.map((b) => ({
            ...b,
            displayValue: toDisplayValue(b.ownershipPercentage ?? 0),
        })),
    );
    const [isDirty, setIsDirty] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const total = rows.reduce(
        (sum, r) => sum + fromDisplayValue(r.displayValue),
        0,
    );
    const isTotalValid = total === 100;
    const showTotalError = isDirty && rows.length > 0 && !isTotalValid;

    const handlePercentageChange = (id: string, value: string) => {
        setRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, displayValue: value } : r)),
        );
        setIsDirty(true);
    };

    const pendingRow = rows.find((r) => r.beneficiaryId === pendingDeleteId);
    const addedByLabel =
        pendingRow != null
            ? `Added by ${names.get(pendingRow.addedByUserId)}`
            : '';

    const confirmDeleteFromAccount = async () => {
        if (pendingDeleteId == null) {
            return;
        }
        setRows((prev) =>
            prev.filter((r) => r.beneficiaryId !== pendingDeleteId),
        );
        setIsUpdating(true);
        await onDelete(pendingDeleteId);
        setIsDirty(true);
        setPendingDeleteId(null);
        setIsUpdating(false);
    };

    const confirmDeleteFromAllAccounts = async () => {
        if (pendingDeleteId == null) {
            return;
        }
        setRows((prev) =>
            prev.filter((r) => r.beneficiaryId !== pendingDeleteId),
        );
        setIsUpdating(true);
        await onDeleteFromAllAccounts(pendingDeleteId);
        setIsDirty(true);
        setPendingDeleteId(null);
        setIsUpdating(false);
    };

    const handleSave = () => {
        setIsUpdating(true);
        onSave(
            rows.map((r) => ({
                id: r.id,
                bankAccountId: r.bankAccountId,
                percentage: fromDisplayValue(r.displayValue),
            })),
        );
        setIsDirty(false);
        setIsUpdating(false);
    };

    if (pendingRow != null) {
        return (
            <RemoveBeneficiaryConfirmation
                beneficiaryName={`${pendingRow.legalFirstName} ${pendingRow.legalLastName}`}
                addedByLabel={addedByLabel}
                accountName={detail.nickname ?? 'Fruitful Cash Account'}
                onBack={() => {
                    setPendingDeleteId(null);
                }}
                onRemoveFromAccount={confirmDeleteFromAccount}
                onRemoveFromAllAccounts={confirmDeleteFromAllAccounts}
                isRemoving={isUpdating}
            />
        );
    }

    return (
        <YStack gap={24} paddingVertical={32}>
            <YStack gap={8}>
                <H5Regular>{detail.nickname} beneficiaries</H5Regular>
                <BodyMediumRegular color="$gray1000">
                    Available funds will be paid out to your beneficiaries in
                    percentages listed below once all account owners are
                    deceased.
                </BodyMediumRegular>
            </YStack>
            <YStack gap={16}>
                {rows.map((row) => {
                    return (
                        <YStack key={row.id}>
                            <LabelRegular padding={8} color="$gray1000">
                                {`${row.legalFirstName} ${row.legalLastName}`}
                            </LabelRegular>
                            <XStack gap={12} alignItems="flex-end">
                                <YStack flex={1} gap={8}>
                                    <PercentInput
                                        value={row.displayValue}
                                        onChangeText={(value) => {
                                            handlePercentageChange(
                                                row.id,
                                                value,
                                            );
                                        }}
                                    />
                                </YStack>
                                <IconButton
                                    variant="tertiary"
                                    icon={Trash2}
                                    onPress={() => {
                                        setPendingDeleteId(row.beneficiaryId);
                                    }}
                                />
                            </XStack>
                        </YStack>
                    );
                })}
            </YStack>
            {showTotalError && (
                <BodySmallRegular color="$red1100">
                    Percentages must add up to 100%. Current total: {total}%.
                </BodySmallRegular>
            )}
            <Button
                text="Save changes"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                disabled={!isDirty || isSaving || !isTotalValid}
                onPress={handleSave}
            />
        </YStack>
    );
};
